import {useEffect, useMemo, useRef, useState} from 'react';

import {Box, ButtonBase, Drawer, Stack, Typography, useMediaQuery} from '@mui/material';

import {API_CONFIG} from '../contentGenerator/config';
import {resolveVars} from '../contentGenerator/formulaUtils';
import {
    productService,
    reconcileProductVars,
} from '../contentGenerator/services/products/productService';
import {useProductContext} from '../shared/ProductContext';

import {C, DEFAULT_LIMITS} from './constants';
import ChatPanel from './components/ChatPanel';
import ListingDetail from './components/ListingDetail';
import {EmptyDetail, ImportPanel} from './components/Panels';
import ReviewConfigsDialog from './components/ReviewConfigsDialog';
import Sidebar from './components/Sidebar';
import {buildBrandContext} from '../shared/brand/brandService';
import {exportReviewListings} from '../shared/exportService';
import {useGmc} from '../shared/gmc/gmcService';
import {useProductDetailFieldSettings} from '../shared/productDetailFieldSettings';
import {useReviewConfigs} from '../shared/reviewConfigs';
import {useReviewAiSettings} from '../shared/reviewAiSettings';
import ResizeHandle from '../shared/ResizeHandle';
import {useResizablePanel} from '../shared/useResizablePanel';
import {trackUsageEvent} from '../shared/usageStats';
import {
    buildReviewConfigPayload,
    buildSavableReviewConfigGroups,
    hydrateReviewConfig,
} from './reviewConfigUtils';
import {
    applyBatchFieldTextUpdates,
    applyWorkflowStatusesToListing,
    buildReviewListings,
    createField,
    filterListings,
    getRelatedListingIds,
    normalizeFieldSubgroupMeta,
    normalizeListing,
    normalizeListings,
    resetListingsFromProducts,
    resolvePublishTargetListings,
    validateListingPublishSafety,
} from './utils';

const getFieldSubgroupKey = (field) =>
    normalizeFieldSubgroupMeta(field?.subgroupMeta)?.groupKey || '';

const getChatTargetKey = (listingId = '', fieldId = '') => `${listingId}:${fieldId}`;
const REVIEW_SIDEBAR_WIDTH = {
    storageKey: 'cr-sidebar-width',
    compact: 320,
    expanded: 460,
    min: 260,
    max: 640,
};

// Counts the source field's position among its same-type peers in the
// listing. When `subgroupAware` is true, peers are further restricted
// to fields sharing the source's subgroupMeta groupKey — so a brushed-
// finish FAQ at position 2 of the whole list might be ordinal 0 inside
// its own subgroup. The propagation logic in updateField/deleteField
// needs that subgroup-scoped ordinal; the variant-tab logic in
// ListingDetail intentionally crosses subgroups and uses the global
// ordinal.
const getFieldTypeOrdinal = (listing, fieldId, {subgroupAware = false} = {}) => {
    const sourceField = listing.fields.find((field) => field.id === fieldId);
    if (!sourceField) return -1;

    const sourceSubgroupKey = subgroupAware
        ? getFieldSubgroupKey(sourceField)
        : null;

    return listing.fields
        .filter((field) => {
            if (field.type !== sourceField.type) return false;
            if (!subgroupAware) return true;
            return getFieldSubgroupKey(field) === sourceSubgroupKey;
        })
        .findIndex((field) => field.id === fieldId);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const countListingFieldsByType = (listings = []) =>
    listings.reduce(
        (acc, listing) => {
            acc.listings += 1;
            (listing.fields || []).forEach((field) => {
                if (field.type === 'title') acc.titles += 1;
                if (field.type === 'description') acc.descriptions += 1;
                if (field.type === 'bullet') acc.bullets += 1;
                if (field.status === 'approved') acc.approvedFields += 1;
            });
            return acc;
        },
        {listings: 0, titles: 0, descriptions: 0, bullets: 0, approvedFields: 0},
    );

// When `subgroupAware` is true, only fields whose subgroupMeta
// groupKey matches the source are eligible candidates. Pass true from
// the update / delete propagation paths so edits to one subgroup
// don't bleed into siblings carrying different subgroup values; leave
// it false (the default) for variant-tab discovery in ListingDetail
// where we intentionally enumerate ACROSS subgroups.
const findMatchingFieldIndex = (
    listing,
    sourceField,
    sourceTypeOrdinal,
    {subgroupAware = false} = {},
) => {
    const sourceSubgroupKey = subgroupAware ? getFieldSubgroupKey(sourceField) : null;
    const isSubgroupCompatible = (field) =>
        !subgroupAware || getFieldSubgroupKey(field) === sourceSubgroupKey;

    // Exact id match — only works for the source listing or by
    // coincidence; linked listings re-generate field ids so this
    // usually misses for them. Still cheap to try first, and still
    // requires subgroup compatibility when that's enabled.
    const exactIndex = listing.fields.findIndex(
        (field) => field.id === sourceField.id && isSubgroupCompatible(field),
    );
    if (exactIndex >= 0) return exactIndex;

    // Type + ordinal among same-type (and same-subgroup, if enabled)
    // peers. Reliable for FAQs and bullets where every variant in a
    // group has the same number of entries in the same order. Try this
    // BEFORE label match because labels can drift if a user manually
    // renames or the AI returned a different "field" key.
    const sameType = listing.fields
        .map((field, index) => ({field, index}))
        .filter(
            ({field}) =>
                field.type === sourceField.type && isSubgroupCompatible(field),
        );
    if (
        sourceTypeOrdinal >= 0 &&
        sourceTypeOrdinal < sameType.length
    ) {
        return sameType[sourceTypeOrdinal].index;
    }

    // Last resort: unique label + type (+ subgroup) match.
    const sameLabelAndType = sameType.filter(
        ({field}) => field.label === sourceField.label,
    );
    if (sameLabelAndType.length === 1) {
        return sameLabelAndType[0].index;
    }

    // Failed to map this field across listings. Surface a one-line
    // warning so cross-listing propagation bugs are easy to spot in
    // devtools. Returning -1 means the caller leaves the linked
    // listing untouched (silent miss otherwise).
    if (typeof console !== 'undefined' && console.warn) {
        console.warn(
            '[review] could not match field across linked listing',
            {
                sourceField: {
                    id: sourceField.id,
                    type: sourceField.type,
                    label: sourceField.label,
                    subgroupKey: getFieldSubgroupKey(sourceField),
                },
                sourceTypeOrdinal,
                subgroupAware,
                targetListingId: listing.id,
                targetFieldTypes: listing.fields.map(
                    (f) =>
                        `${f.type}:${f.label}:${getFieldSubgroupKey(f) || '-'}`,
                ),
            },
        );
    }
    return -1;
};

const TOAST_COLORS = {
    success: {bg: '#E1F5EE', border: '#1D9E7544', text: '#1D9E75', icon: '✓'},
    error: {bg: '#FAECE7', border: '#D85A3044', text: '#D85A30', icon: '✕'},
    warning: {bg: '#FAEEDA', border: '#BA751744', text: '#BA7517', icon: '!'},
    info: {bg: '#E6F1FB', border: '#185FA544', text: '#185FA5', icon: 'i'},
};

function Toast({toast, onClose}) {
    const colors = TOAST_COLORS[toast.type] || TOAST_COLORS.info;

    useEffect(() => {
        const timer = setTimeout(onClose, toast.type === 'error' ? 6000 : 4000);
        return () => clearTimeout(timer);
    }, [toast, onClose]);

    return (
        <Box sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 16px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            background: colors.bg,
            boxShadow: '0 4px 20px rgba(0,0,0,.12)',
            animation: 'popIn .2s ease both',
            maxWidth: 480,
        }}>
            <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                background: `${colors.text}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, color: colors.text, flexShrink: 0,
            }}>
                {colors.icon}
            </Box>
            <Typography sx={{fontSize: 13, fontWeight: 600, color: colors.text, lineHeight: 1.3}}>
                {toast.message}
            </Typography>
            <ButtonBase
                onClick={onClose}
                sx={{
                    marginLeft: 'auto', flexShrink: 0,
                    fontSize: 14, color: colors.text, opacity: 0.5,
                    cursor: 'pointer', padding: '0 2px',
                }}>
                ×
            </ButtonBase>
        </Box>
    );
}

export default function ContentReview({apiKey = '', brandProfile = null, isActive = true}) {
    const isMobile = useMediaQuery('(max-width:1100px)');
    const [listings, setListings] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [filterMode, setFilterMode] = useState('all');
    const {
        width: sidebarWidth,
        setWidth: setSidebarWidth,
        startDrag: startSidebarDrag,
    } = useResizablePanel({
        storageKey: REVIEW_SIDEBAR_WIDTH.storageKey,
        defaultWidth: REVIEW_SIDEBAR_WIDTH.expanded,
        min: REVIEW_SIDEBAR_WIDTH.min,
        max: REVIEW_SIDEBAR_WIDTH.max,
    });
    // The toggle still snaps between compact and expanded presets; drag
    // can land anywhere between min and max. `sidebarExpanded` is the
    // visual indicator state — true once the rail is closer to the
    // expanded preset than the compact one.
    const sidebarExpanded =
        sidebarWidth >= (REVIEW_SIDEBAR_WIDTH.compact + REVIEW_SIDEBAR_WIDTH.expanded) / 2;
    const handleToggleSidebarWidth = () =>
        setSidebarWidth(
            sidebarExpanded ? REVIEW_SIDEBAR_WIDTH.compact : REVIEW_SIDEBAR_WIDTH.expanded,
        );
    const [showImport, setShowImport] = useState(false);
    const [chatTarget, setChatTarget] = useState(null);
    const [chatHistories, setChatHistories] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [mobileChatOpen, setMobileChatOpen] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState(null);
    const [showReviewConfigs, setShowReviewConfigs] = useState(false);
    const [publishingToShopify, setPublishingToShopify] = useState(false);
    const [publishBatchState, setPublishBatchState] = useState(null);
    const [pushingToGmc, setPushingToGmc] = useState(false);
    const [gmcBatchState, setGmcBatchState] = useState(null);
    const [toast, setToast] = useState(null); // {message, type: 'success'|'error'|'info'}
    const gmc = useGmc();
    const productDetailFieldSettings = useProductDetailFieldSettings();
    const reviewConfigs = useReviewConfigs();
    const {
        customActions,
        customCommands,
        setCustomActions,
        setCustomCommands,
    } = useReviewAiSettings();

    // ── Product context (shared with ContentGenerator) ──
    const {
        products,
        selectedProductIds,
        selectedProducts,
        currentProductIndex,
        setCurrentProductIndex,
        handleProductChange,
        productSource,
        vars,
        managedProductVarNames,
        activeProfileSnapshot,
        setReviewGroupForProducts,
        replaceReviewWorkspace,
        reviewGroupByProductId,
        resultsByProductId,
    } = useProductContext();

    // ── Sync listings from selected products (prefer generated content) ──
    useEffect(() => {
        if (!selectedProducts.length) {
            setListings([]);
            return;
        }

        setListings((currentListings) =>
            buildReviewListings(
                selectedProducts,
                resultsByProductId,
                currentListings,
                reviewGroupByProductId,
                brandProfile?.shopifyMapping,
            ),
        );
    }, [brandProfile?.shopifyMapping, reviewGroupByProductId, resultsByProductId, selectedProducts]);

    useEffect(() => {
        if (!listings.length) {
            setActiveId(null);
            setChatTarget(null);
            return;
        }
        if (!listings.some((listing) => listing.id === activeId)) {
            setActiveId(listings[0]?.id || null);
            setChatTarget(null);
        }
    }, [activeId, listings]);

    useEffect(() => {
        if (!isMobile) {
            setSidebarOpen(false);
            setMobileChatOpen(false);
        }
    }, [isMobile]);

    const profileSettings = activeProfileSnapshot?.settings || null;
    const brandContext = useMemo(() => buildBrandContext(brandProfile), [brandProfile]);
    const charLimits = useMemo(
        () => ({
            title: profileSettings?.title?.maxChars || DEFAULT_LIMITS.title,
            description:
                profileSettings?.description?.maxChars || DEFAULT_LIMITS.description,
            bullet: profileSettings?.bullets?.maxChars || DEFAULT_LIMITS.bullet,
        }),
        [profileSettings],
    );
    const activeListing = listings.find((listing) => listing.id === activeId) || null;
    const desktopSidebarWidth = sidebarWidth;
    const getListingProduct = (listing) =>
        listing?.sourceProductId
            ? selectedProducts.find((product) => product.id === listing.sourceProductId) || null
            : null;
    const getListingInputImages = (listing) => {
        const listingProduct = getListingProduct(listing);
        const candidates = [
            ...(Array.isArray(listingProduct?.raw?.images) ? listingProduct.raw.images : []),
            ...(listingProduct?.imageUrl
                ? [
                      {
                          url: listingProduct.imageUrl,
                          name: listingProduct.name || listingProduct.sku || 'Product image',
                      },
                  ]
                : []),
        ];
        const seen = new Set();

        return candidates
            .map((image) => ({
                url: typeof image === 'string' ? image : image?.url || image?.image_url || '',
                name:
                    typeof image === 'string'
                        ? listingProduct?.name || listingProduct?.sku || 'Product image'
                        : image?.name ||
                          image?.label ||
                          listingProduct?.name ||
                          listingProduct?.sku ||
                          'Product image',
            }))
            .filter((image) => {
                if (!image.url || seen.has(image.url)) return false;
                seen.add(image.url);
                return true;
            });
    };
    const getListingVars = (listing) => {
        const productVars = getListingProduct(listing)?.vars || [];
        if (!productVars.length) return vars;
        return reconcileProductVars(vars, productVars, managedProductVarNames);
    };
    const publishStatusLabel = publishBatchState?.total
        ? `Publishing ${publishBatchState.completed}/${publishBatchState.total}`
        : '';
    const gmcStatusLabel = gmcBatchState?.total
        ? `Pushing ${gmcBatchState.completed}/${gmcBatchState.total}`
        : '';
    const activeListingSourceProductIndex = useMemo(
        () =>
            activeListing?.sourceProductId
                ? selectedProducts.findIndex(
                      (product) => product.id === activeListing.sourceProductId,
                  )
                : -1,
        [activeListing?.sourceProductId, selectedProducts],
    );
    const activeListingVars = useMemo(
        () => getListingVars(activeListing),
        [activeListing, managedProductVarNames, selectedProducts, vars],
    );
    const listingPublishValidationById = useMemo(
        () =>
            Object.fromEntries(
                listings.map((listing) => {
                    const listingProduct = listing?.sourceProductId
                        ? selectedProducts.find(
                              (product) => product.id === listing.sourceProductId,
                          ) || null
                        : null;
                    const listingVars = listingProduct?.vars?.length
                        ? reconcileProductVars(
                              vars,
                              listingProduct.vars,
                              managedProductVarNames,
                          )
                        : vars;

                    return [
                        listing.id,
                        validateListingPublishSafety(listing, listingVars),
                    ];
                }),
            ),
        [listings, managedProductVarNames, selectedProducts, vars],
    );
    const activeListingPublishValidation = activeListing
        ? listingPublishValidationById[activeListing.id] || null
        : null;
    const filteredListings = useMemo(
        () =>
            filterListings(
                listings,
                '',
                filterMode,
                listingPublishValidationById,
            ),
        [filterMode, listingPublishValidationById, listings],
    );
    const activeChatListing =
        listings.find((listing) => listing.id === chatTarget?.listingId) || null;
    const activeChatField =
        activeChatListing?.fields.find((field) => field.id === chatTarget?.fieldId) || null;
    const eligibleReviewConfigGroups = useMemo(
        () => buildSavableReviewConfigGroups(listings),
        [listings],
    );

    // Bidirectional sync between product index and active listing
    const prevProductIndexRef = useRef(currentProductIndex);
    useEffect(() => {
        if (!isActive) return;

        const productIndexChanged = prevProductIndexRef.current !== currentProductIndex;
        prevProductIndexRef.current = currentProductIndex;

        if (productIndexChanged) {
            // Product index changed (Prev/Next buttons) — navigate to a listing from that product
            const currentProduct = selectedProducts[currentProductIndex];
            if (!currentProduct) return;
            if (activeListing?.sourceProductId === currentProduct.id) return;

            const targetListing = listings.find(
                (listing) => listing.sourceProductId === currentProduct.id,
            );
            if (targetListing) {
                setActiveId(targetListing.id);
                setChatTarget(null);
            }
        } else if (
            activeListingSourceProductIndex >= 0 &&
            activeListingSourceProductIndex !== currentProductIndex
        ) {
            // Active listing changed — sync product index to match
            setCurrentProductIndex(activeListingSourceProductIndex);
        }
    }, [
        activeListingSourceProductIndex,
        activeListing?.sourceProductId,
        currentProductIndex,
        isActive,
        listings,
        selectedProducts,
        setCurrentProductIndex,
    ]);

    const updateLinkedListings = (listingId, updater) => {
        setListings((currentListings) => {
            const linkedIds = new Set(getRelatedListingIds(currentListings, listingId));

            return currentListings.map((listing) =>
                linkedIds.has(listing.id) ? normalizeListing(updater(listing)) : listing,
            );
        });
    };

    const updateField = (listingId, fieldId, patch) => {
        setListings((currentListings) => {
            const sourceListing = currentListings.find((listing) => listing.id === listingId);
            if (!sourceListing) return currentListings;

            const sourceField = sourceListing.fields.find((field) => field.id === fieldId);
            if (!sourceField) return currentListings;

            const linkedIds = new Set(getRelatedListingIds(currentListings, listingId));
            // Subgroup-aware propagation: edits to a subgrouped field
            // (e.g. a Brushed Nickel FAQ in a multi-finish group) must
            // only land on siblings sharing the same subgroup key, not
            // on the Chrome / Matte Black versions.
            const sourceTypeOrdinal = getFieldTypeOrdinal(sourceListing, fieldId, {
                subgroupAware: true,
            });

            return currentListings.map((listing) => {
                if (!linkedIds.has(listing.id)) return listing;

                const matchingFieldIndex =
                    listing.id === listingId
                        ? listing.fields.findIndex((field) => field.id === fieldId)
                        : findMatchingFieldIndex(
                              listing,
                              sourceField,
                              sourceTypeOrdinal,
                              {subgroupAware: true},
                          );

                if (matchingFieldIndex < 0) return listing;

                return normalizeListing({
                    ...listing,
                    fields: listing.fields.map((field, index) =>
                        index === matchingFieldIndex
                            ? {
                                  ...field,
                                  ...patch,
                              }
                            : field,
                    ),
                });
            });
        });
    };

    const updateFieldsBatch = (updates) => {
        setListings((currentListings) =>
            applyBatchFieldTextUpdates(currentListings, updates),
        );
    };

    const addField = (listingId, field) => {
        updateLinkedListings(listingId, (listing) => ({
            ...listing,
            fields: [...listing.fields, createField(field)],
        }));
    };

    const deleteField = (listingId, fieldId) => {
        setListings((currentListings) => {
            const sourceListing = currentListings.find((listing) => listing.id === listingId);
            if (!sourceListing) return currentListings;

            const sourceField = sourceListing.fields.find((field) => field.id === fieldId);
            if (!sourceField) return currentListings;

            const linkedIds = new Set(getRelatedListingIds(currentListings, listingId));
            // Same subgroup-aware rule as updateField: deletion of a
            // subgroup-specific field shouldn't drop the equivalent
            // field on siblings in different subgroups.
            const sourceTypeOrdinal = getFieldTypeOrdinal(sourceListing, fieldId, {
                subgroupAware: true,
            });

            return currentListings.map((listing) => {
                if (!linkedIds.has(listing.id)) return listing;

                const matchingFieldIndex =
                    listing.id === listingId
                        ? listing.fields.findIndex((field) => field.id === fieldId)
                        : findMatchingFieldIndex(
                              listing,
                              sourceField,
                              sourceTypeOrdinal,
                              {subgroupAware: true},
                          );

                if (matchingFieldIndex < 0) return listing;

                return normalizeListing({
                    ...listing,
                    fields: listing.fields.filter(
                        (_field, index) => index !== matchingFieldIndex,
                    ),
                });
            });
        });
        if (chatTarget?.listingId === listingId && chatTarget?.fieldId === fieldId) {
            setChatTarget(null);
        }
    };

    const deleteListing = (listingId) => {
        const listingToDelete = listings.find((listing) => listing.id === listingId);
        const remainingListings = listings.filter((listing) => listing.id !== listingId);
        const nextActiveId = activeId === listingId
            ? remainingListings[0]?.id ?? null
            : activeId;

        setListings((currentListings) =>
            currentListings.filter((listing) => listing.id !== listingId),
        );
        if (listingToDelete?.sourceProductId) {
            handleProductChange(
                selectedProductIds.filter(
                    (productId) => productId !== listingToDelete.sourceProductId,
                ),
            );
        }
        if (activeId === listingId) {
            setActiveId(nextActiveId);
        }
        if (chatTarget?.listingId === listingId || activeId === listingId) {
            setChatTarget(null);
        }
    };

    const importListings = (nextListings) => {
        const normalized = normalizeListings(nextListings);
        if (!normalized.length) return;

        // Build a case-insensitive SKU index over the loaded product catalog
        // so the order of `name` vs `sku` columns and stray whitespace in
        // user import files don't break matching.
        const productEntries = Array.from(products.values());
        const skuIndex = new Map();
        productEntries.forEach((product) => {
            const candidates = [product.sku, product.id, product?.raw?.variants?.[0]?.sku]
                .filter((value) => typeof value === 'string' && value.trim());
            candidates.forEach((value) => {
                const key = value.trim().toLowerCase();
                if (!skuIndex.has(key)) skuIndex.set(key, product);
            });
        });

        const matchedProductIds = [];
        const unmatchedSkus = [];
        const stamped = normalized.map((listing) => {
            const lookupKey = (listing.sku || '').trim().toLowerCase();
            if (!lookupKey) return listing;
            const product = skuIndex.get(lookupKey);
            if (!product) {
                unmatchedSkus.push(listing.sku);
                return listing;
            }
            if (!matchedProductIds.includes(product.id)) {
                matchedProductIds.push(product.id);
            }
            // Stamping sourceProductId so publish/push flows can find the
            // backing Shopify product. Falling back to the listing's own SKU
            // for the buildReviewListings rebuild effect, which keys by
            // `p-${product.id}`.
            return {
                ...listing,
                id: `p-${product.id}`,
                sourceProductId: product.id,
                name: listing.name || product.name || product.sku,
                category: listing.category || product.category || '',
            };
        });

        // Merge matched product IDs into the existing selection so the
        // listings rebuild effect (driven by `selectedProducts`) keeps the
        // imported listings around. Without this, the next rebuild would
        // wipe them since they wouldn't correspond to any selected product.
        if (matchedProductIds.length) {
            const nextSelection = Array.from(new Set([...selectedProductIds, ...matchedProductIds]));
            handleProductChange(nextSelection);
        }

        // Sync imported groups to the shared product context so the
        // generator module sees them too. Imported listings carry their
        // group via `superSku`; we batch them by group and call
        // `setReviewGroupForProducts` once per group rather than once per
        // listing to avoid spamming context updates.
        const productIdsByGroup = new Map();
        stamped.forEach((listing) => {
            if (!listing.sourceProductId || !listing.superSku) return;
            if (!productIdsByGroup.has(listing.superSku)) {
                productIdsByGroup.set(listing.superSku, []);
            }
            productIdsByGroup.get(listing.superSku).push(listing.sourceProductId);
        });
        productIdsByGroup.forEach((productIds, superSku) => {
            setReviewGroupForProducts(productIds, superSku);
        });

        // Splice the imported listings into the current listings array,
        // replacing any auto-built entries for the same product so the
        // user-supplied content wins.
        setListings((currentListings) => {
            const importedById = new Map(stamped.map((listing) => [listing.id, listing]));
            const merged = currentListings.map((listing) =>
                importedById.has(listing.id) ? importedById.get(listing.id) : listing,
            );
            const existingIds = new Set(merged.map((listing) => listing.id));
            const additions = stamped.filter((listing) => !existingIds.has(listing.id));
            return [...merged, ...additions];
        });

        setActiveId(stamped[0].id);
        setChatTarget(null);
        setShowImport(false);

        const matchedCount = matchedProductIds.length;
        const totalCount = stamped.length;
        if (!matchedCount) {
            setToast({
                message: `Imported ${totalCount} listing(s), but none of the SKUs matched the loaded catalog. Load products from Shopify first.`,
                type: 'warning',
            });
        } else if (unmatchedSkus.length) {
            setToast({
                message: `Imported ${totalCount} listing(s). Matched ${matchedCount} to products; ${unmatchedSkus.length} SKU(s) not in catalog.`,
                type: 'warning',
            });
        } else {
            setToast({
                message: `Imported ${totalCount} listing(s) and matched all to products.`,
                type: 'success',
            });
        }
    };

    // Approve every field on every listing currently in review.
    // Previously this was scoped to the linked group via
    // updateLinkedListings — but the intent of "Approve all" is the
    // full session: all variants, all groups, all SKUs. Signature /
    // group-key mismatches no longer leave stragglers unapproved.
    const approveAll = () => {
        setListings((currentListings) =>
            currentListings.map((listing) =>
                normalizeListing({
                    ...listing,
                    fields: listing.fields.map((field) => ({
                        ...field,
                        status: 'approved',
                    })),
                }),
            ),
        );
    };

    const addChatMessage = (listingId, fieldId, message) => {
        const targetKey = getChatTargetKey(listingId, fieldId);

        setChatHistories((current) => ({
            ...current,
            [targetKey]: [...(current[targetKey] || []), message],
        }));
    };

    const setSuperSku = (listingId, superSku) => {
        setListings((currentListings) => {
            const sourceListing = currentListings.find((listing) => listing.id === listingId);
            if (!sourceListing) return currentListings;

            const templateListing =
                superSku && currentListings.find(
                    (listing) =>
                        listing.id !== listingId && listing.superSku === superSku,
                );

            return currentListings.map((listing) => {
                if (listing.id !== listingId) return listing;

                return normalizeListing({
                    ...listing,
                    superSku,
                    generatorSignature:
                        templateListing?.generatorSignature || listing.generatorSignature,
                    editLinkKey:
                        templateListing?.editLinkKey || listing.editLinkKey,
                    fields: templateListing
                        ? templateListing.fields.map(({id: _id, ...field}) =>
                              createField(field),
                          )
                        : listing.fields,
                });
            });
        });
        const sourceProductId = listings.find((listing) => listing.id === listingId)?.sourceProductId;
        if (sourceProductId) {
            setReviewGroupForProducts([sourceProductId], superSku);
        }
    };

    const refreshAllFromShopify = () => {
        const refreshResult = resetListingsFromProducts(
            listings,
            selectedProducts,
            brandProfile?.shopifyMapping,
        );

        if (!refreshResult.refreshedCount) {
            setToast({
                message: 'No selected SKUs could be matched to the loaded Shopify catalog.',
                type: 'info',
            });
            return;
        }

        setListings(refreshResult.listings);
        const missingCount = refreshResult.missingListingIds.length;
        setToast({
            message: missingCount
                ? `Refreshed ${refreshResult.refreshedCount} SKU(s); ${missingCount} could not be matched.`
                : `Refreshed all ${refreshResult.refreshedCount} selected SKU(s) from Shopify.`,
            type: missingCount ? 'warning' : 'success',
        });
        trackUsageEvent('refresh.shopify.batch', {
            refreshedListings: refreshResult.refreshedCount,
            missingListings: missingCount,
        });
    };

    const downloadListing = (listingId, format, scope) => {
        const listing = listings.find((item) => item.id === listingId);
        if (!listing) return;
        const listingForExport =
            scope === 'approved'
                ? applyWorkflowStatusesToListing(
                      listing,
                      listingPublishValidationById?.[listing.id] || null,
                  )
                : listing;
        exportReviewListings([listingForExport], format, scope, getListingVars);
        trackUsageEvent('export.review', {
            format,
            scope,
            source: 'listing',
            ...countListingFieldsByType([listingForExport]),
        });
    };

    const saveReviewConfig = async (name, existingConfigId = '') => {
        const payload = buildReviewConfigPayload({
            name,
            listings,
        });

        if (!payload.groups.length) {
            throw new Error('Create a group with at least two SKUs before saving.');
        }

        const savedConfig = await reviewConfigs.saveConfig({
            id: existingConfigId,
            config: payload,
        });

        setToast({
            message: existingConfigId
                ? `Updated "${savedConfig.name}".`
                : `Saved "${savedConfig.name}".`,
            type: 'success',
        });
        trackUsageEvent('review_config.save', {
            groups: savedConfig.counts.groups,
            listings: savedConfig.counts.listings,
            updated: Boolean(existingConfigId),
        });
        return savedConfig;
    };

    const loadReviewConfig = (config) => {
        const hydrated = hydrateReviewConfig({
            config,
            products,
        });

        if (!hydrated.listings.length) {
            setToast({
                message: 'Could not load this config. None of its saved groups still have 2 matching SKUs in the current catalog.',
                type: 'warning',
            });
            return;
        }

        setListings(hydrated.listings);
        setActiveId(hydrated.listings[0]?.id || null);
        setChatTarget(null);
        setChatHistories({});
        setShowImport(false);
        setShowReviewConfigs(false);

        if (typeof replaceReviewWorkspace === 'function') {
            replaceReviewWorkspace({
                productIds: hydrated.selectedProductIds,
                reviewGroupByProductId: hydrated.reviewGroupByProductId,
            });
        } else {
            handleProductChange(hydrated.selectedProductIds);
            Object.entries(hydrated.reviewGroupByProductId).forEach(([productId, groupKey]) => {
                setReviewGroupForProducts([productId], groupKey);
            });
        }

        const warnings = [];
        if (hydrated.missingSkus.length) {
            warnings.push(`${hydrated.missingSkus.length} missing SKU(s) skipped`);
        }
        if (hydrated.skippedGroups.length) {
            warnings.push(`${hydrated.skippedGroups.length} group(s) skipped`);
        }

        setToast({
            message: warnings.length
                ? `Loaded "${config.name}". ${warnings.join('. ')}.`
                : `Loaded "${config.name}".`,
            type: warnings.length ? 'warning' : 'success',
        });
        trackUsageEvent('review_config.load', {
            groups: Object.keys(
                hydrated.listings.reduce((accumulator, listing) => ({
                    ...accumulator,
                    [listing.superSku]: true,
                }), {}),
            ).length,
            listings: hydrated.listings.length,
            missingSkus: hydrated.missingSkus.length,
            skippedGroups: hydrated.skippedGroups.length,
        });
    };

    const deleteReviewConfig = async (config) => {
        await reviewConfigs.removeConfig(config.id);
        setToast({message: `Deleted "${config.name}".`, type: 'success'});
        trackUsageEvent('review_config.delete', {
            groups: config.counts?.groups || 0,
            listings: config.counts?.listings || 0,
        });
    };

    const isApproved = (field) => field?.status === 'approved';

    const publishSingleListing = async (listing, {enabledSections = null} = {}) => {
        const listingVars = getListingVars(listing);
        const publishValidation = validateListingPublishSafety(listing, listingVars);
        const shopifyPublishState = publishValidation.destinations.shopify;
        const resolve = (text) => text && text.includes('{{') && listingVars.length ? resolveVars(text, listingVars) : (text || '');
        const mapping = brandProfile?.shopifyMapping || {};
        // Section gate: when the caller passes enabledSections from the
        // pre-publish dialog, treat any unchecked section as disabled —
        // the rest of this function then short-circuits its content
        // collection so that section is omitted from the Shopify update
        // payload. Without enabledSections, default to publishing every
        // section (legacy behavior).
        const isSectionEnabled = (key) =>
            enabledSections ? Boolean(enabledSections[key]) : true;

        if (!shopifyPublishState.hasEligibleContent) {
            return {
                name: listing.name,
                skipped: true,
                skippedReason: shopifyPublishState.blockedApprovedFieldIds.length
                    ? 'validation'
                    : 'no_content',
            };
        }

        const titleField = listing.fields.find((f) => f.type === 'title');
        const descField = listing.fields.find((f) => f.type === 'description');
        const titleIsEligible =
            isSectionEnabled('title') &&
            titleField &&
            publishValidation.fieldsById[titleField.id]?.isEligibleByDestination.shopify;
        const descIsEligible =
            isSectionEnabled('description') &&
            descField &&
            publishValidation.fieldsById[descField.id]?.isEligibleByDestination.shopify;

        // Bullet rule: all-or-nothing. Publishing only some bullets would
        // produce an incomplete bullet list in Shopify, which is worse than
        // not touching them at all. Only push bullets if every non-empty
        // bullet is approved.
        const bulletsEnabled = isSectionEnabled('bullets');
        const bulletFields = listing.fields.filter((f) => f.type === 'bullet' && f.text.trim());
        const eligibleBulletFields = bulletFields.filter(
            (field) =>
                publishValidation.fieldsById[field.id]?.isEligibleByDestination.shopify,
        );
        const allBulletsApproved = bulletFields.length > 0 && bulletFields.every(isApproved);
        const allBulletsEligible =
            bulletFields.length > 0 && eligibleBulletFields.length === bulletFields.length;
        const bulletTexts = bulletsEnabled && allBulletsApproved && allBulletsEligible
            ? eligibleBulletFields.map((f) => resolve(f.text))
            : [];

        const product = selectedProducts.find(
            (p) => p.sku === listing.sourceProductId || String(p.shopifyId) === String(listing.sourceProductId),
        );
        const shopifyId = product?.shopifyId || product?.raw?.id;
        const variantId = product?.variantId || product?.raw?.variant_id;
        if (!shopifyId) throw new Error(`Could not resolve Shopify ID for ${listing.name}`);

        const update = {};
        const metafields = [];

        const titleMapping = mapping.title || {type: 'native', source: 'product.title'};
        const resolvedTitle = titleIsEligible ? resolve(titleField?.text) : '';
        if (resolvedTitle) {
            if (titleMapping.type === 'metafield' && titleMapping.source) {
                const [ns, key] = titleMapping.source.split('.');
                metafields.push({namespace: ns, key, value: resolvedTitle, type: 'single_line_text_field'});
            } else {
                update.title = resolvedTitle;
            }
        }

        const descMapping = mapping.description || {type: 'native', source: 'product.body_html'};
        const resolvedDesc = descIsEligible ? resolve(descField?.text?.trim() || '') : '';
        const bulletMapping = mapping.bullets || {type: 'html_list'};

        if (descMapping.type === 'metafield' && descMapping.source) {
            const [ns, key] = descMapping.source.split('.');
            if (resolvedDesc) metafields.push({namespace: ns, key, value: resolvedDesc, type: 'multi_line_text_field'});
        } else {
            const bodyParts = [];
            if (resolvedDesc) bodyParts.push(`<p>${resolvedDesc}</p>`);
            if (bulletMapping.type === 'html_list' && bulletTexts.length) {
                bodyParts.push(`<ul>${bulletTexts.map((t) => `<li>${t}</li>`).join('')}</ul>`);
            }
            if (bodyParts.length) update.body_html = bodyParts.join('');
        }

        if (bulletMapping.type === 'metafield' && bulletMapping.source && bulletTexts.length) {
            const [ns, key] = bulletMapping.source.split('.');
            metafields.push({namespace: ns, key, value: JSON.stringify(bulletTexts), type: 'list.single_line_text_field'});
        }

        // FAQs follow the same all-or-nothing rule as bullets: if any
        // non-empty FAQ on the listing is unapproved or invalid, push no
        // FAQs at all. The FAQPage JSON-LD schema is a single broadcast
        // unit — pushing a partial set would publish an incomplete list
        // marked as canonical. When all are approved, overwrite both
        // sibling metafields (canonical data + rendered JSON-LD).
        const faqsEnabled = isSectionEnabled('faqs');
        const faqFields = listing.fields.filter(
            (f) =>
                f.type === 'faq' &&
                (f.question || '').trim() &&
                (f.text || '').trim(),
        );
        const eligibleFaqFields = faqFields.filter(
            (field) =>
                publishValidation.fieldsById[field.id]?.isEligibleByDestination.shopify,
        );
        const allFaqsApproved = faqFields.length > 0 && faqFields.every(isApproved);
        const allFaqsEligible =
            faqFields.length > 0 && eligibleFaqFields.length === faqFields.length;
        const faqsToPush =
            faqsEnabled && allFaqsApproved && allFaqsEligible
                ? eligibleFaqFields.map((field) => ({
                      question: resolve(field.question).trim(),
                      answer: resolve(field.text).trim(),
                  }))
                : [];

        if (faqsEnabled && faqFields.length) {
            // For the json-type metafield, send a valid empty array when
            // nothing approved — Shopify rejects '' for json metafields.
            // The schema field is a plain text field so empty string is
            // fine there.
            const faqsValue = faqsToPush.length ? JSON.stringify(faqsToPush) : '[]';
            const schemaValue = faqsToPush.length
                ? JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: faqsToPush.map((faq) => ({
                          '@type': 'Question',
                          name: faq.question,
                          acceptedAnswer: {
                              '@type': 'Answer',
                              text: faq.answer,
                          },
                      })),
                  })
                : '';

            // `$app:contentlab` reserves the namespace under this app's
            // identity in Shopify, matching the metafield definitions
            // auto-created server-side on first publish.
            metafields.push({
                namespace: '$app:contentlab',
                key: 'faqs',
                value: faqsValue,
                type: 'json',
            });
            metafields.push({
                namespace: '$app:contentlab',
                key: 'faqs_schema',
                value: schemaValue,
                type: 'multi_line_text_field',
            });
        }

        // Nothing approved → nothing to publish. Skip the API call entirely.
        if (!Object.keys(update).length && !metafields.length) {
            return {name: listing.name, skipped: true, skippedReason: 'no_content'};
        }

        await productService.updateProduct(shopifyId, {
            ...update,
            ...(metafields.length ? {metafields, variantId} : {}),
        });
        return {name: listing.name, skipped: false};
    };

    const publishToShopify = async (listing, options = {}) => {
        if (!listing?.sourceProductId || publishingToShopify) return;
        setPublishingToShopify(true);
        try {
            const group = listing.superSku;
            const toPublish = resolvePublishTargetListings(
                listings,
                listing,
                options.targetListingIds,
            );
            const total = toPublish.length;
            const batchSize = API_CONFIG.SHOPIFY_PUBLISH_BATCH_SIZE;
            const batchDelayMs = API_CONFIG.SHOPIFY_PUBLISH_BATCH_DELAY_MS;

            const successes = [];
            const validationSkipped = [];
            const noContentSkipped = [];
            const errors = [];
            setPublishBatchState({
                completed: 0,
                total,
                currentListingName: toPublish[0]?.name || '',
            });

            for (let batchStart = 0; batchStart < total; batchStart += batchSize) {
                const batch = toPublish.slice(batchStart, batchStart + batchSize);

                for (const [batchIndex, targetListing] of batch.entries()) {
                    const completed = batchStart + batchIndex;
                    setPublishBatchState({
                        completed,
                        total,
                        currentListingName: targetListing.name,
                    });

                    try {
                        const result = await publishSingleListing(targetListing, options);
                        if (result?.skipped) {
                            if (result.skippedReason === 'validation') {
                                validationSkipped.push(result.name);
                            } else {
                                noContentSkipped.push(result.name);
                            }
                        } else {
                            successes.push(result.name);
                        }
                    } catch (err) {
                        errors.push(`${targetListing.name}: ${err.message}`);
                    }
                }

                setPublishBatchState({
                    completed: Math.min(batchStart + batch.length, total),
                    total,
                    currentListingName:
                        batchStart + batch.length < total ? toPublish[batchStart + batch.length]?.name || '' : '',
                });

                if (batchDelayMs > 0 && batchStart + batch.length < total) {
                    await sleep(batchDelayMs);
                }
            }

            const hasSuccess = successes.length > 0;
            const hasErrors = errors.length > 0;
            const hasValidationSkipped = validationSkipped.length > 0;
            const hasNoContentSkipped = noContentSkipped.length > 0;

            if (hasSuccess && (hasErrors || hasValidationSkipped || hasNoContentSkipped)) {
                const parts = [`Published ${successes.length} listing(s)`];
                if (hasValidationSkipped) {
                    parts.push(`${validationSkipped.length} blocked by SKU validation`);
                }
                if (hasNoContentSkipped) {
                    parts.push(
                        `${noContentSkipped.length} skipped (no approved safe content)`,
                    );
                }
                if (hasErrors) parts.push(`${errors.length} failed`);
                setToast({message: `${parts.join('. ')}.`, type: 'warning'});
            } else if (hasErrors) {
                setToast({message: `Publish failed: ${errors[0]}`, type: 'error'});
            } else if (hasSuccess) {
                if (toPublish.length > 1) {
                    setToast({
                        message:
                            group && !Array.isArray(options.targetListingIds)
                                ? `Published ${successes.length} listings in group "${group}"`
                                : `Published ${successes.length} listings`,
                        type: 'success',
                    });
                } else {
                    setToast({message: `Published "${successes[0]}"`, type: 'success'});
                }
            } else if (hasValidationSkipped) {
                setToast({
                    message:
                        validationSkipped.length > 1
                            ? `${validationSkipped.length} listings blocked by missing or mismatched SKU context.`
                            : `Nothing published — "${validationSkipped[0]}" is blocked by missing or mismatched SKU context.`,
                    type: 'warning',
                });
            } else if (hasNoContentSkipped) {
                setToast({
                    message:
                        noContentSkipped.length > 1
                            ? `Nothing to publish — no approved safe content in any of ${noContentSkipped.length} listings.`
                            : 'Nothing to publish — no approved safe content. Approve a field or fix flagged fields first.',
                    type: 'warning',
                });
            }
            trackUsageEvent('publish.shopify', {
                attempted: total,
                published: successes.length,
                failed: errors.length,
                skippedValidation: validationSkipped.length,
                skippedNoContent: noContentSkipped.length,
            });
        } catch (err) {
            trackUsageEvent('publish.shopify.failed', {message: err.message});
            setToast({message: `Publish failed: ${err.message}`, type: 'error'});
        } finally {
            setPublishBatchState(null);
            setPublishingToShopify(false);
        }
    };

    const pushSingleListingToGmc = async (listing, {enabledSections = null} = {}) => {
        const listingVars = getListingVars(listing);
        const publishValidation = validateListingPublishSafety(listing, listingVars);
        const gmcPublishState = publishValidation.destinations.gmc;
        const resolve = (text) =>
            text && text.includes('{{') && listingVars.length
                ? resolveVars(text, listingVars)
                : text || '';
        const isSectionEnabled = (key) =>
            enabledSections ? Boolean(enabledSections[key]) : true;

        if (!gmcPublishState.hasEligibleContent) {
            return {
                name: listing.name,
                skipped: true,
                skippedReason: gmcPublishState.blockedApprovedFieldIds.length
                    ? 'validation'
                    : 'no_content',
                successCount: 0,
                errorCount: 0,
                firstError: '',
            };
        }

        const titleField = listing.fields.find((field) => field.type === 'title');
        const descField = listing.fields.find((field) => field.type === 'description');

        const title =
            isSectionEnabled('title') &&
            titleField &&
            publishValidation.fieldsById[titleField.id]?.isEligibleByDestination.gmc
                ? resolve(titleField?.text)
                : '';
        const description =
            isSectionEnabled('description') &&
            descField &&
            publishValidation.fieldsById[descField.id]?.isEligibleByDestination.gmc
                ? resolve(descField?.text?.trim() || '')
                : '';

        // Nothing approved → nothing to push. Skip the API call entirely.
        if (!title && !description) {
            return {
                name: listing.name,
                skipped: true,
                skippedReason: 'no_content',
                successCount: 0,
                errorCount: 0,
                firstError: '',
            };
        }

        const product = selectedProducts.find(
            (entry) =>
                entry.sku === listing.sourceProductId
                || String(entry.shopifyId) === String(listing.sourceProductId),
        );
        const shopifyId = product?.shopifyId || product?.raw?.id || listing.sourceProductId;
        if (!shopifyId) {
            throw new Error(`Could not resolve Shopify ID for ${listing.name}`);
        }
        const variantId = product?.variantId || product?.raw?.variant_id || null;

        const result = await gmc.pushProduct(shopifyId, {
            title,
            description,
            variantIds: variantId ? [variantId] : undefined,
        });

        return {
            name: listing.name,
            skipped: false,
            successCount: result.successCount || 0,
            errorCount: result.errorCount || 0,
            firstError: result.results?.find((entry) => entry.error)?.error || '',
        };
    };

    const pushToGmc = async (listing, options = {}) => {
        if (!listing?.sourceProductId || pushingToGmc) return;

        if (!gmc.status.connected) {
            setToast({
                message: 'Connect Google Merchant Center first (open settings from the listing toolbar).',
                type: 'warning',
            });
            return;
        }
        if (!gmc.status.settings.merchantId || !gmc.status.settings.supplementalFeedId) {
            setToast({
                message: 'Set Merchant ID and supplemental feed ID in Google Merchant settings.',
                type: 'warning',
            });
            return;
        }

        setPushingToGmc(true);
        try {
            const toPush = resolvePublishTargetListings(
                listings,
                listing,
                options.targetListingIds,
            );
            const total = toPush.length;
            const batchSize = API_CONFIG.GMC_PUBLISH_BATCH_SIZE;
            const batchDelayMs = API_CONFIG.GMC_PUBLISH_BATCH_DELAY_MS;

            const successes = [];
            const validationSkipped = [];
            const noContentSkipped = [];
            const failures = [];
            let variantSuccess = 0;
            let variantError = 0;

            setGmcBatchState({
                completed: 0,
                total,
                currentListingName: toPush[0]?.name || '',
            });

            for (let batchStart = 0; batchStart < total; batchStart += batchSize) {
                const batch = toPush.slice(batchStart, batchStart + batchSize);

                for (const [batchIndex, targetListing] of batch.entries()) {
                    const completed = batchStart + batchIndex;
                    setGmcBatchState({
                        completed,
                        total,
                        currentListingName: targetListing.name,
                    });

                    try {
                        const entry = await pushSingleListingToGmc(targetListing, options);
                        if (entry.skipped) {
                            if (entry.skippedReason === 'validation') {
                                validationSkipped.push(entry.name);
                            } else {
                                noContentSkipped.push(entry.name);
                            }
                            continue;
                        }
                        variantSuccess += entry.successCount;
                        variantError += entry.errorCount;
                        if (entry.errorCount && !entry.successCount) {
                            failures.push(`${entry.name}: ${entry.firstError || 'failed'}`);
                        } else {
                            successes.push(entry.name);
                        }
                    } catch (err) {
                        failures.push(`${targetListing.name}: ${err.message}`);
                    }
                }

                setGmcBatchState({
                    completed: Math.min(batchStart + batch.length, total),
                    total,
                    currentListingName:
                        batchStart + batch.length < total
                            ? toPush[batchStart + batch.length]?.name || ''
                            : '',
                });

                if (batchDelayMs > 0 && batchStart + batch.length < total) {
                    await sleep(batchDelayMs);
                }
            }

            const hasSuccess = successes.length > 0;
            const hasFailures = failures.length > 0;
            const hasValidationSkipped = validationSkipped.length > 0;
            const hasNoContentSkipped = noContentSkipped.length > 0;

            if (hasSuccess && (hasFailures || hasValidationSkipped || hasNoContentSkipped)) {
                const parts = [`Pushed ${successes.length} listing(s) to Google Merchant`];
                if (hasValidationSkipped) {
                    parts.push(`${validationSkipped.length} blocked by SKU validation`);
                }
                if (hasNoContentSkipped) {
                    parts.push(
                        `${noContentSkipped.length} skipped (no approved safe content)`,
                    );
                }
                if (hasFailures) parts.push(`${failures.length} failed`);
                setToast({message: `${parts.join('. ')}.`, type: 'warning'});
            } else if (hasFailures) {
                setToast({message: `Google Merchant push failed: ${failures[0]}`, type: 'error'});
            } else if (hasSuccess) {
                if (toPush.length > 1) {
                    setToast({
                        message: `Pushed ${successes.length} listings to Google Merchant.`,
                        type: 'success',
                    });
                } else {
                    setToast({
                        message: `Pushed "${successes[0]}" to Google Merchant.`,
                        type: 'success',
                    });
                }
            } else if (hasValidationSkipped) {
                setToast({
                    message:
                        validationSkipped.length > 1
                            ? `${validationSkipped.length} listings blocked by missing or mismatched SKU context.`
                            : `Nothing pushed — "${validationSkipped[0]}" is blocked by missing or mismatched SKU context.`,
                    type: 'warning',
                });
            } else if (hasNoContentSkipped) {
                setToast({
                    message:
                        noContentSkipped.length > 1
                            ? `Nothing to push — no approved safe title or description in any of ${noContentSkipped.length} listings.`
                            : 'Nothing to push — approve a title or description or fix flagged fields first.',
                    type: 'warning',
                });
            }

            if (variantError && variantSuccess) {
                console.warn(`GMC push: ${variantError} variant override(s) failed.`);
            }
            trackUsageEvent('publish.gmc', {
                attempted: total,
                pushedListings: successes.length,
                variantSuccess,
                variantError,
                failedListings: failures.length,
                skippedValidation: validationSkipped.length,
                skippedNoContent: noContentSkipped.length,
            });
        } catch (err) {
            trackUsageEvent('publish.gmc.failed', {message: err.message});
            setToast({message: `Google Merchant push failed: ${err.message}`, type: 'error'});
        } finally {
            setGmcBatchState(null);
            setPushingToGmc(false);
        }
    };

    const chatPanel = activeChatListing && activeChatField ? (
        <ChatPanel
            apiKey={apiKey}
            charLimit={charLimits[activeChatField.type] || 0}
            customActions={customActions}
            field={activeChatField}
            inputImages={getListingInputImages(activeChatListing)}
            listing={activeChatListing}
            messages={
                chatHistories[
                    getChatTargetKey(activeChatListing.id, activeChatField.id)
                ] || []
            }
            brandContext={brandContext}
            profileSettings={profileSettings}
            vars={activeListingVars}
            onClose={() => {
                setChatTarget(null);
                setMobileChatOpen(false);
            }}
            onMessage={(fieldId, message) =>
                addChatMessage(activeChatListing.id, fieldId, message)
            }
            onSetCustomActions={setCustomActions}
            onUpdateField={(fieldId, patch) =>
                updateField(activeChatListing.id, fieldId, patch)
            }
        />
    ) : null;

    const sidebar = (
        <Sidebar
            activeId={activeId}
            activeProfile={activeProfileSnapshot}
            allListings={listings}
            brandProfile={brandProfile}
            charLimits={charLimits}
            customCommands={customCommands}
            filterMode={filterMode}
            filteredListings={filteredListings}
            publishValidationByListingId={listingPublishValidationById}
            onDeleteListing={(listingId) => {
                const listing = listings.find((l) => l.id === listingId);
                setPendingConfirm({
                    label: `Delete "${listing?.name || 'listing'}"?`,
                    onConfirm: () => deleteListing(listingId),
                });
            }}
            onExportApproved={(format) => {
                const exportedListings = listings.map((listing) =>
                    applyWorkflowStatusesToListing(
                        listing,
                        listingPublishValidationById?.[listing.id] || null,
                    ),
                );
                exportReviewListings(
                    exportedListings,
                    format,
                    'approved',
                    getListingVars,
                );
                trackUsageEvent('export.review', {
                    format,
                    scope: 'approved',
                    source: 'sidebar',
                    ...countListingFieldsByType(exportedListings),
                });
            }}
            onExportCsv={(format) => {
                exportReviewListings(listings, format, 'all', getListingVars);
                trackUsageEvent('export.review', {
                    format,
                    scope: 'all',
                    source: 'sidebar',
                    ...countListingFieldsByType(listings),
                });
            }}
            onSelect={(listingId) => {
                setActiveId(listingId);
                setChatTarget(null);
                setSidebarOpen(false);
            }}
            onSetCustomCommands={setCustomCommands}
            onSetFilterMode={setFilterMode}
            onSetSuperSku={setSuperSku}
            onShowConfigs={() => {
                setShowReviewConfigs(true);
                setSidebarOpen(false);
            }}
            onShowImport={() => {
                setShowImport((current) => !current);
                setSidebarOpen(false);
            }}
            onToggleWidth={isMobile ? null : handleToggleSidebarWidth}
            sidebarExpanded={sidebarExpanded}
        />
    );

    return (
        <Box
            sx={{
                fontFamily: "'DM Sans',system-ui,sans-serif",
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100vh',
                minWidth: 0,
                minHeight: 0,
                background: 'var(--cg-canvas)',
                color: C.ink,
                border: `1px solid ${C.border2}`,
                borderRadius: '16px',
                overflow: 'hidden',
            }}>
            {isMobile && (
                <Stack
                    direction="row"
                    gap="8px"
                    sx={{
                        padding: '12px 14px',
                        borderBottom: `1px solid ${C.border}`,
                        background: C.surface,
                    }}>
                    <ButtonBase
                        onClick={() => setSidebarOpen(true)}
                        sx={{
                            border: `1px solid ${C.border}`,
                            background: C.surfaceAlt,
                            color: C.ink,
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontFamily: 'inherit',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}>
                        Listings
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => setMobileChatOpen(true)}
                        disabled={!chatPanel}
                        sx={{
                            border: `1px solid ${chatPanel ? `${C.purple}33` : C.border}`,
                            background: chatPanel ? C.purpleL : C.surfaceAlt,
                            color: chatPanel ? C.purple : C.textSoft,
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontFamily: 'inherit',
                            fontWeight: 700,
                            cursor: chatPanel ? 'pointer' : 'default',
                        }}>
                        Chat
                    </ButtonBase>
                </Stack>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                        ? '1fr'
                        : `${desktopSidebarWidth}px 6px minmax(0,1fr)`,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                {!isMobile && sidebar}
                {!isMobile && (
                    <ResizeHandle
                        onStartDrag={startSidebarDrag}
                        color={C.teal}
                        ariaLabel="Resize listings sidebar"
                    />
                )}

                <Box sx={{minWidth: 0, minHeight: 0, overflow: 'hidden', background: C.mist}}>
                    {showImport ? (
                        <Box sx={{padding: '18px'}}>
                            <ImportPanel onImport={importListings} />
                        </Box>
                    ) : activeListing ? (
                        <ListingDetail
                            allListings={listings}
                            apiKey={apiKey}
                            charLimits={charLimits}
                            chatTarget={chatTarget}
                            chatPanel={chatPanel}
                            getListingInputImages={getListingInputImages}
                            customCommands={customCommands}
                            getListingProduct={getListingProduct}
                            getListingVars={getListingVars}
                            isMobile={isMobile}
                            listing={activeListing}
                            publishValidation={activeListingPublishValidation}
                            publishValidationByListingId={listingPublishValidationById}
                            brandContext={brandContext}
                            brandProfile={brandProfile}
                            profileSettings={profileSettings}
                            productSource={productSource}
                            productDetailFieldSettings={productDetailFieldSettings}
                            onSaveProductDetailFieldSettings={
                                productDetailFieldSettings.saveSettings
                            }
                            vars={activeListingVars}
                            onAddField={(field) => addField(activeListing.id, field)}
                            onApproveAll={approveAll}
                            onDeleteField={(listingId, fieldId) => {
                                const targetListing =
                                    listings.find((item) => item.id === listingId) ||
                                    activeListing;
                                const field = targetListing?.fields.find((f) => f.id === fieldId);
                                setPendingConfirm({
                                    label: `Delete "${field?.label || 'field'}"?`,
                                    onConfirm: () => deleteField(listingId, fieldId),
                                });
                            }}
                            onDownload={(format, scope) => downloadListing(activeListing.id, format, scope)}
                            onRefreshFromShopify={refreshAllFromShopify}
                            onPublishToShopify={publishToShopify}
                            publishingToShopify={publishingToShopify}
                            publishStatusLabel={publishStatusLabel}
                            gmc={gmc}
                            onPushToGmc={pushToGmc}
                            pushingToGmc={pushingToGmc}
                            gmcStatusLabel={gmcStatusLabel}
                            onOpenChat={(listingId, fieldId) => {
                                setChatTarget(fieldId ? {listingId, fieldId} : null);
                                if (isMobile && fieldId) setMobileChatOpen(true);
                            }}
                            onSelectListing={(listingId) => {
                                setActiveId(listingId);
                                setChatTarget(null);
                            }}
                            onSetSuperSku={setSuperSku}
                            onUpdateField={(listingId, fieldId, patch) =>
                                updateField(listingId, fieldId, patch)
                            }
                            onUpdateFieldsBatch={updateFieldsBatch}
                            onNotify={setToast}
                        />
                    ) : (
                        <EmptyDetail />
                    )}
                </Box>
            </Box>

            <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
                <Box sx={{width: 320, maxWidth: '85vw', height: '100%'}}>{sidebar}</Box>
            </Drawer>

            <Drawer anchor="right" open={mobileChatOpen && Boolean(chatPanel)} onClose={() => setMobileChatOpen(false)}>
                <Box sx={{width: 360, maxWidth: '92vw', height: '100%'}}>{chatPanel}</Box>
            </Drawer>

            {pendingConfirm && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap="10px"
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 9999,
                        background: C.surface,
                        border: `1px solid ${C.border2}`,
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: C.shadow,
                        fontFamily: "'DM Sans',system-ui,sans-serif",
                    }}>
                    <Typography component="span" sx={{fontSize: 13, color: C.ink}}>{pendingConfirm.label}</Typography>
                    <ButtonBase
                        onClick={() => {
                            pendingConfirm.onConfirm();
                            setPendingConfirm(null);
                        }}
                        sx={{
                            border: 'none',
                            background: C.coral,
                            color: '#fff',
                            borderRadius: '8px',
                            padding: '5px 12px',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}>
                        Delete
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => setPendingConfirm(null)}
                        sx={{
                            border: `1px solid ${C.border}`,
                            background: 'transparent',
                            color: C.textSoft,
                            borderRadius: '8px',
                            padding: '5px 12px',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}>
                        Cancel
                    </ButtonBase>
                </Stack>
            )}

            <ReviewConfigsDialog
                configs={reviewConfigs.configs}
                eligibleGroups={eligibleReviewConfigGroups}
                error={reviewConfigs.error}
                loading={reviewConfigs.loading}
                onClose={() => setShowReviewConfigs(false)}
                onDelete={deleteReviewConfig}
                onLoad={loadReviewConfig}
                onSave={saveReviewConfig}
                open={showReviewConfigs}
                saving={reviewConfigs.saving}
            />

            {/* Toast notification */}
            {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
        </Box>
    );
}
